import { Component } from '@theme/component';
import { onDocumentLoaded, setHeaderMenuStyle, debounce, lockScroll, unlockScroll } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @extends {Component}
 */
class HeaderMenu extends Component {
  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('click', this.#onDocumentClick);

    this.refs.railItem?.forEach((item) => {
      item.addEventListener('pointerenter', this.#onRailItemPointerMove);
      item.addEventListener('pointermove', this.#onRailItemPointerMove);
      item.addEventListener('pointerleave', this.#onRailItemPointerLeave);
      item.addEventListener('focusin', this.#onRailItemEnter);
      item.addEventListener('click', this.#onRailItemClick);
    });
    this.refs.railClose?.forEach((button) =>
      button.addEventListener('click', () => {
        const item = this.#state.activeItem;
        this.#deactivate();
        item?.focus();
      })
    );

    this.#inertObserver.observe(this, { attributes: true, attributeFilter: ['inert'], subtree: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('click', this.#onDocumentClick);
    this.#inertObserver.disconnect();

    if (this.#hoverCloseTimeout) clearTimeout(this.#hoverCloseTimeout);
    clearTimeout(this.#railSwitchTimeout);
  }

  #hoverCloseTimeout = null;
  #railSwitchTimeout = null;
  #railPointer = null;
  #hoverItem = null;

  get #supportsHover() {
    return typeof matchMedia === 'function' && matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  activateOnHover = (event) => {
    if (!this.#supportsHover || !(event.target instanceof Element)) return;

    const item = findMenuItemWithin(event.target);
    if (!item || !findSubmenu(item)) return;

    if (this.#hoverCloseTimeout) {
      clearTimeout(this.#hoverCloseTimeout);
      this.#hoverCloseTimeout = null;
    }

    this.#activate(item);
    this.#hoverItem = item;
  };

  scheduleDeactivateOnHover = (event) => {
    if (!this.#supportsHover || !(event.target instanceof Element)) return;

    const item = findMenuItemWithin(event.target);
    if (!item || item !== this.#state.activeItem) return;

    if (this.#hoverCloseTimeout) clearTimeout(this.#hoverCloseTimeout);

    this.#hoverCloseTimeout = setTimeout(() => {
      this.#hoverCloseTimeout = null;
      this.#deactivate(item);
    }, 200);
  };

  #inertObserver = new MutationObserver((mutations) => {
    const activeSubmenu = this.#state.activeItem && findSubmenu(this.#state.activeItem);
    if (!activeSubmenu) return;

    for (const mutation of mutations) {
      if (mutation.target === activeSubmenu && activeSubmenu.inert) {
        activeSubmenu.inert = false;
      }
    }
  });

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  /**
   * Close the open submenu on Escape and restore focus to its trigger.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    const item = this.#state.activeItem;
    if (!item) return;

    this.#deactivate(item);
    item.focus();
  };

  #onDocumentClick = (event) => {
    if (!this.#state.activeItem) return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.#deactivate();
  };

  toggle = (event) => {
    const item = findMenuItem(event.target);
    if (!item) return;

    const submenu = findSubmenu(item);
    if (!submenu) return;

    event.preventDefault();

    if (item === this.#state.activeItem) {
      if (this.#hoverItem === item) {
        this.#hoverItem = null;
        return;
      }
      this.#deactivate(item);
    } else {
      this.#activate(item);
    }
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {HTMLElement} item
   */
  #activate(item) {
    if (!this.headerComponent || item === this.#state.activeItem) return;

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) previousSubmenu.inert = true;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';
    document.body.classList.add('megamenu_open');
    lockScroll(this);

    const submenu = findSubmenu(item);

    if (submenu) {
      this.#resetRail(submenu);
      this.dispatchEvent(new MegaMenuHoverEvent());

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Expose the open submenu to focus and assistive tech. Collapsed submenus stay
      // inert so their contents cannot be reached (visibility:hidden alone can be
      // overridden by descendants that re-assert visibility).
      submenu.inert = false;
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item != this.#state.activeItem) return;

    const submenu = findSubmenu(item);
    this.#hoverItem = null;

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (!document.querySelector('header-menu[aria-expanded="true"]')) {
      document.body.classList.remove('megamenu_open');
    }
    unlockScroll(this);

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      submenu.inert = true;
      this.#resetRail(submenu);
    }
  };

  #resetRail(submenu) {
    clearTimeout(this.#railSwitchTimeout);
    this.#railPointer = null;
    submenu.querySelectorAll('.menu-rail__item--active').forEach((el) => el.classList.remove('menu-rail__item--active'));
    submenu.querySelectorAll('.menu-rail__detail--active').forEach((el) => el.classList.remove('menu-rail__detail--active'));
  }

  /**
   * Rail items switch on hover, except while the pointer is heading right towards the open detail: then
   * the switch waits until the pointer rests, so crossing other items on the way (diagonally, or slowly
   * on a trackpad) keeps the detail the visitor is reaching for.
   * @param {PointerEvent} event
   */
  #onRailItemPointerMove = (event) => {
    const item = event.currentTarget;
    const last = this.#railPointer;
    const dx = last ? event.clientX - last.x : 0;
    const dy = last ? event.clientY - last.y : 0;
    // pointerenter and pointermove arrive together at the same point; only real movement counts
    if (last && dx === 0 && dy === 0) return;

    this.#railPointer = { x: event.clientX, y: event.clientY };
    clearTimeout(this.#railSwitchTimeout);
    if (!(item instanceof HTMLElement) || item.classList.contains('menu-rail__item--active')) return;

    const detailOpen = item.closest('.menu-list__submenu')?.querySelector('.menu-rail__detail--active');

    if (detailOpen && dx > 0 && dx * 2 >= Math.abs(dy)) {
      this.#railSwitchTimeout = setTimeout(() => this.#activateRailItem(item), 300);
      return;
    }

    this.#activateRailItem(item);
  };

  #onRailItemPointerLeave = () => {
    clearTimeout(this.#railSwitchTimeout);
  };

  #onRailItemEnter = (event) => {
    if (event.currentTarget instanceof HTMLElement) this.#activateRailItem(event.currentTarget);
  };

  /**
   * @param {MouseEvent} event
   */
  #onRailItemClick = (event) => {
    const link = event.target instanceof Element ? event.target.closest('.menu-rail__link') : null;
    if (link?.getAttribute('href') === '#') event.preventDefault();
  };

  /**
   * @param {HTMLElement} item
   */
  #activateRailItem(item) {
    const submenu = item.closest('.menu-list__submenu');
    if (!submenu) return;

    const index = item.dataset.railIndex;
    const detail = submenu.querySelector(`.menu-rail__detail[data-rail-index="${index}"]`);

    submenu.querySelectorAll('.menu-rail__item--active').forEach((el) => el.classList.remove('menu-rail__item--active'));
    submenu.querySelectorAll('.menu-rail__detail--active').forEach((el) => el.classList.remove('menu-rail__detail--active'));

    if (!detail) return;

    item.classList.add('menu-rail__item--active');
    detail.classList.add('menu-rail__detail--active');
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {EventTarget | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  const item = element.closest('[ref="menuitem"]');
  return item instanceof HTMLElement ? item : null;
}

function findMenuItemWithin(element) {
  if (!(element instanceof Element)) return null;

  const item = element.querySelector(':scope > [ref="menuitem"]');
  return item instanceof HTMLElement ? item : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest?.('.menu-list__list-item');
  const submenu = listItem?.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
